import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { Lead, EmailHistory } from '@otw/models/leads/lead';
import { Vehicle } from '@otw/models/inventory/inventory';
import { useInventory } from 'hooks/inventory/useInventory';
import { useLeads } from 'hooks/admin/useLeads';
import { useTheme } from '@mui/material/styles';
import { 
  useSimilarityCalculation,
  VehicleWithSimilarity
} from 'hooks/useSimilarityCalculation';
import { attachVehicleToLead, removeVehicleFromLead } from '../util';

interface VehicleAttachmentContextType {
  // Vehicle data
  attachedVehicleIds: string[];
  linkedVehicles: Vehicle[];
  suggestedVehicles: VehicleWithSimilarity[];
  filteredVehicles: VehicleWithSimilarity[];
  exactMatches: VehicleWithSimilarity[];
  similarMatches: VehicleWithSimilarity[];
  isLoadingVehicles: boolean;
  
  // Truck type filters
  selectedTruckTypes: string[];
  setSelectedTruckTypes: React.Dispatch<React.SetStateAction<string[]>>;
  allTruckTypes: {typeKey: string, displayName: string, size: string[] | string}[];
  
  // UI state
  attachingVehicle: string | null;
  
  // Inventory search state
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  searchPerformed: boolean;
  setSearchPerformed: React.Dispatch<React.SetStateAction<boolean>>;
  filterType: string;
  setFilterType: React.Dispatch<React.SetStateAction<string>>;
  filterStatus: string[];
  setFilterStatus: React.Dispatch<React.SetStateAction<string[]>>;
  searchResults: VehicleWithSimilarity[];
  handleInventorySearch: () => void;
  
  // Actions
  setAttachedVehicleIds: React.Dispatch<React.SetStateAction<string[]>>;
  refreshLinkedVehicles: () => void;
  refreshSuggestedVehicles: () => void;
  handleAttachVehicleToLead: (vehicle: Vehicle) => Promise<string[] | null>;
  handleRemoveVehicleFromLead: (vehicle: Vehicle) => Promise<string[] | null>;
  handleTruckTypeFilterChange: (typeKey: string) => void;
}

const VehicleAttachmentContext = createContext<VehicleAttachmentContextType | undefined>(undefined);

export const VehicleAttachmentProvider: React.FC<{
  children: React.ReactNode;
  lead: Lead;
}> = ({ children, lead }) => {
  const { vehicles } = useInventory("master_inventory");
  const { updateLead } = useLeads();
  const theme = useTheme();
  
  // Use the similarity calculation hook
  const { 
    getDisplayName, 
    calculateSimilarityForVehicles,
    handleTruckTypeFilterChange: filterTruckTypes
  } = useSimilarityCalculation();
  
  // Vehicle data states
  const [attachedVehicleIds, setAttachedVehicleIds] = useState<string[]>([]);
  const [linkedVehicles, setLinkedVehicles] = useState<Vehicle[]>([]);
  const [suggestedVehicles, setSuggestedVehicles] = useState<VehicleWithSimilarity[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<VehicleWithSimilarity[]>([]);
  const [exactMatches, setExactMatches] = useState<VehicleWithSimilarity[]>([]);
  const [similarMatches, setSimilarMatches] = useState<VehicleWithSimilarity[]>([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(true);
  
  // Truck type filter states
  const [allTruckTypes, setAllTruckTypes] = useState<{typeKey: string, displayName: string, size: string[] | string}[]>([]);
  const [selectedTruckTypes, setSelectedTruckTypes] = useState<string[]>([]);
  
  // UI states
  const [attachingVehicle, setAttachingVehicle] = useState<string | null>(null);
  
  // Inventory search states
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchPerformed, setSearchPerformed] = useState<boolean>(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string[]>(['Available']);
  const [searchResults, setSearchResults] = useState<VehicleWithSimilarity[]>([]);

  // Use refs to avoid unnecessary re-renders
  const vehiclesRef = useRef<Vehicle[] | null>(null);
  const leadRef = useRef<Lead | null>(null);

  // Initialize attached vehicle IDs from lead
  useEffect(() => {
    if (lead) {
      setAttachedVehicleIds(lead.truckIds || []);
    }
  }, [lead]);

  // Update linkedVehicles when attachedVehicleIds or vehicles change
  useEffect(() => {
    refreshLinkedVehicles();
  }, [vehicles, attachedVehicleIds]);
  
  // Calculate suggested vehicles when vehicles or lead changes
  useEffect(() => {
    refreshSuggestedVehicles();
  }, [vehicles, lead]);

  // Only update the refs when the actual data changes
  useEffect(() => {
    if (vehicles) {
      vehiclesRef.current = vehicles;
    }
  }, [vehicles]);

  useEffect(() => {
    if (lead) {
      leadRef.current = lead;
    }
  }, [lead]);

  // Function to refresh linked vehicles
  const refreshLinkedVehicles = useCallback(() => {
    if (!vehicles || !attachedVehicleIds.length) {
      setLinkedVehicles([]);
      return;
    }
    
    const linked = vehicles.filter(vehicle => 
      attachedVehicleIds.includes(vehicle.id)
    );
    setLinkedVehicles(linked);
  }, [vehicles, attachedVehicleIds]);

  // Function to refresh suggested vehicles
  const refreshSuggestedVehicles = useCallback(() => {
    if (!vehicles || !lead) {
      setSuggestedVehicles([]);
      setFilteredVehicles([]);
      setExactMatches([]);
      setSimilarMatches([]);
      setIsLoadingVehicles(false);
      return;
    }
    
    setIsLoadingVehicles(true);
    
    try {
      // Calculate similarity for all vehicles
      const vehiclesWithSimilarity = calculateSimilarityForVehicles(vehicles, lead);
      
      // Set suggested vehicles
      setSuggestedVehicles(vehiclesWithSimilarity);
      
      // Extract unique truck types from the suggested vehicles
      const truckTypes = Array.from(new Set(
        vehiclesWithSimilarity
          .filter(v => v.typeKey)
          .map(v => v.typeKey as string)
      ));
      
      // Set all truck types
      const uniqueTruckTypes = truckTypes.map(typeKey => {
        const [_, indexStr] = typeKey.split('-');
        const index = parseInt(indexStr);
        const truckType = lead.truckRequest?.types?.[index];
        
        return {
          typeKey,
          displayName: truckType ? getDisplayName(truckType) : 'Unknown',
          size: truckType?.size || [] // Handle as array
        };
      });
      
      setAllTruckTypes(uniqueTruckTypes);
      
      // Initialize selected truck types if empty
      if (selectedTruckTypes.length === 0) {
        setSelectedTruckTypes(truckTypes);
      }
      
      // Set filtered vehicles to all suggested vehicles initially
      setFilteredVehicles(vehiclesWithSimilarity);
      
      // Separate exact and similar matches
      setExactMatches(vehiclesWithSimilarity.filter(v => v.similarityLevel === 'exact'));
      setSimilarMatches(vehiclesWithSimilarity.filter(v => v.similarityLevel === 'similar'));
    } catch (error) {
      console.error('Error calculating vehicle similarity:', error);
      setSuggestedVehicles([]);
      setFilteredVehicles([]);
      setExactMatches([]);
      setSimilarMatches([]);
    } finally {
      setIsLoadingVehicles(false);
    }
  }, [vehicles, lead, selectedTruckTypes.length, calculateSimilarityForVehicles, getDisplayName]);

  // Function to handle inventory search
  const handleInventorySearch = useCallback(() => {
    if (!vehicles) {
      setSearchResults([]);
      setSearchPerformed(true);
      return;
    }
    
    setSearchPerformed(true);
    
    const results = vehicles
      .filter(vehicle => {
        // Filter by status (default: Available). "all" or an empty selection includes every status.
        const selectedStatuses = filterStatus.filter((status) => status !== 'all');
        if (selectedStatuses.length > 0 && !filterStatus.includes('all')) {
          const vehicleStatus = (vehicle.status || '').trim().toLowerCase();
          const allowedStatuses = new Set(
            selectedStatuses.map((status) => status.trim().toLowerCase()),
          );
          if (!allowedStatuses.has(vehicleStatus)) {
            return false;
          }
        }

        // Filter by type if a specific type is selected
        if (filterType !== 'all' && vehicle.type?.toLowerCase() !== filterType.toLowerCase()) {
          return false;
        }

        // Search in various vehicle fields
        const searchLower = searchQuery.toLowerCase();
        return (
          vehicle.stock_number?.toLowerCase().includes(searchLower) ||
          vehicle.vin?.toLowerCase().includes(searchLower) ||
          `${vehicle.year} ${vehicle.make} ${vehicle.model}`.toLowerCase().includes(searchLower) ||
          vehicle.type?.toLowerCase().includes(searchLower) ||
          vehicle.size?.toLowerCase().includes(searchLower) ||
          vehicle.location?.toLowerCase().includes(searchLower) ||
          vehicle.who?.toLowerCase().includes(searchLower)  // Add search by "who" field
        );
      })
      .map(vehicle => ({
        ...vehicle,
        similarityLevel: 'none' as const
      })) as VehicleWithSimilarity[];
    
    setSearchResults(results);
  }, [vehicles, searchQuery, filterType, filterStatus]);
  
  // Function to attach vehicle to lead
  const handleAttachVehicleToLead = useCallback(async (vehicle: Vehicle): Promise<string[] | null> => {
    return await attachVehicleToLead(
      vehicle,
      lead,
      attachedVehicleIds,
      updateLead,
      setAttachingVehicle,
      setAttachedVehicleIds
    );
  }, [lead, attachedVehicleIds, updateLead]);
  
  // Function to remove vehicle from lead
  const handleRemoveVehicleFromLead = useCallback(async (vehicle: Vehicle): Promise<string[] | null> => {
    return await removeVehicleFromLead(
      vehicle,
      lead,
      attachedVehicleIds,
      updateLead,
      setAttachingVehicle,
      setAttachedVehicleIds
    );
  }, [lead, attachedVehicleIds, updateLead]);
  
  // Function to handle truck type filter changes
  const handleTruckTypeFilterChange = useCallback((typeKey: string) => {
    filterTruckTypes(
      typeKey,
      suggestedVehicles
    );
  }, [filterTruckTypes, suggestedVehicles]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    // Vehicle data
    attachedVehicleIds, 
    linkedVehicles, 
    suggestedVehicles,
    filteredVehicles,
    exactMatches,
    similarMatches,
    isLoadingVehicles,
    
    // Truck type filters
    selectedTruckTypes,
    setSelectedTruckTypes,
    allTruckTypes,
    
    // UI state
    attachingVehicle,
    
    // Inventory search state
    searchQuery,
    setSearchQuery,
    searchPerformed,
    setSearchPerformed,
    filterType,
    setFilterType,
    filterStatus,
    setFilterStatus,
    searchResults,
    handleInventorySearch,
    
    // Actions
    setAttachedVehicleIds,
    refreshLinkedVehicles,
    refreshSuggestedVehicles,
    handleAttachVehicleToLead,
    handleRemoveVehicleFromLead,
    handleTruckTypeFilterChange
  }), [
    attachedVehicleIds, 
    linkedVehicles, 
    suggestedVehicles,
    filteredVehicles,
    exactMatches,
    similarMatches,
    isLoadingVehicles,
    selectedTruckTypes,
    setSelectedTruckTypes,
    allTruckTypes,
    attachingVehicle,
    searchQuery,
    searchPerformed,
    filterType,
    filterStatus,
    searchResults,
    handleInventorySearch,
    refreshLinkedVehicles,
    refreshSuggestedVehicles,
    handleAttachVehicleToLead,
    handleRemoveVehicleFromLead,
    handleTruckTypeFilterChange
  ]);

  return (
    <VehicleAttachmentContext.Provider value={contextValue}>
      {children}
    </VehicleAttachmentContext.Provider>
  );
};

export const useVehicleAttachment = () => {
  const context = useContext(VehicleAttachmentContext);
  if (context === undefined) {
    throw new Error('useVehicleAttachment must be used within a VehicleAttachmentProvider');
  }
  return context;
}; 